import { trpc } from '@tg/app-core';
import { GOALS, LEVELS, pmKey } from '@tg/domain';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { age, fullDate, iso, shortDate } from '../lib/dates';
import { fmtN, initials, phoneShow, pmName, useCatalog, useMaxes, useMe, useToday } from '../lib/data';
import { useLocal } from '../lib/storage';
import { histOf, num, type MaxRow } from '../lib/workout';
import { Chart, delta, dltCls } from '../ui/Chart';
import { I } from '../ui/icons';
import { AchFinal, AchShelf, StreakShow, streakQuote, type AchId } from '../ui/FirstSteps';
import { enqueue } from '../ui/achQueue';
import { FormSheet, PickSheet, toast } from '../ui/overlay';
import { Tabs } from '../ui/parts';

/* P0. Профиль: сверху данные клиента, ниже табы «Рекорды» и «Замеры».
   Рекорды и замеры — те же, что у тренера; от 1ПМ считается вес в тренировках. */
const MEAS = [
  ['weight', 'Вес', 'кг'],
  ['waist', 'Талия', 'см'],
  ['chest', 'Грудь', 'см'],
  ['hips', 'Бёдра', 'см'],
  ['fat', 'Жир', '%'],
] as const;
type MKey = (typeof MEAS)[number][0];
type MRow = { id?: string; source?: string; date: string } & Partial<Record<MKey, number | null>>;

export function Profile() {
  const nav = useNavigate();
  const today = useToday();
  const me = useMe();
  const meas = trpc.clientApp.measurements.useQuery(undefined, { staleTime: 60_000 });
  const [tab, setTab] = useLocal<'pr' | 'meas'>('ptab', 'pr');
  const [edit, setEdit] = useState(false);
  /* Свой значок на полке — снова финал (с «Поделиться») */
  const [achOpen, setAchOpen] = useState<AchId | null>(null);
  const [streakOpen, setStreakOpen] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();
  const upd = trpc.clientApp.updateProfile.useMutation({ onSettled: () => utils.clientApp.me.invalidate() });

  const c = me.data;
  const rows = ((meas.data || []) as unknown as MRow[]).slice().sort((a, b) => a.date.localeCompare(b.date));
  const lastW = [...rows].reverse().find((r) => r.weight != null)?.weight ?? c?.weight ?? null;
  const tr = c?.trainer;

  /* Фото — сразу по выбору файла: квадрат 320 px, JPEG */
  const onPhoto = (f: File | undefined) => {
    if (!f) return;
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      const n = 320;
      const cv = document.createElement('canvas');
      const m = Math.min(img.width, img.height);
      cv.width = cv.height = n;
      cv.getContext('2d')!.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, n, n);
      URL.revokeObjectURL(url);
      const photo = cv.toDataURL('image/jpeg', 0.85);
      utils.clientApp.me.setData(undefined, (x) => (x ? { ...x, photo } : x));
      upd.mutate({ photo }, { onSuccess: () => toast('Фото обновлено'), onError: () => toast('Фото не сохранилось — нет связи') });
    };
    img.src = url;
  };

  const f = (k: string, v: string | null | undefined) => (
    <div className="pf">
      <s>{k}</s>
      <b>{v || '—'}</b>
    </div>
  );
  return (
    <>
      <header className="hhead">
        <div className="wmk">Профиль</div>
        <button className="ib hgear" onClick={() => nav('/settings')} aria-label="Настройки">
          <I n="gear" />
        </button>
      </header>
      <div className="pg prof">
        {c ? (
          <>
            <section className="card pcard">
              <div className="pid">
                <button className="pav ph" onClick={() => file.current?.click()} aria-label="Сменить фото">
                  {c.photo ? <img src={c.photo} alt="" /> : initials(c.first, c.last)}
                  <i>
                    <I n="camera" />
                  </i>
                </button>
                <div>
                  <b>{[c.first, c.last].filter(Boolean).join(' ')}</b>
                  <s>
                    {c.sex === 'f' ? 'Женщина' : 'Мужчина'}
                    {age(c.born, today) ? ', ' + age(c.born, today) : ''}
                    {c.group ? ' · ' + c.group.name : ''}
                  </s>
                </div>
                <button className="ib pedit" onClick={() => setEdit(true)} aria-label="Изменить данные">
                  <I n="pen" />
                </button>
              </div>
              <input ref={file} type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} />
              <div className="pgrid">
                {f('Телефон', phoneShow(c.phone))}
                {f('Уровень', c.level)}
                {f('Рост', c.height ? fmtN(c.height) + ' см' : '')}
                {f('Вес', lastW != null ? fmtN(lastW, true) + ' кг' : '')}
              </div>
              <div className="pgoal">
                <div>
                  <s>Основная цель</s>
                  <b>{c.goal || '—'}</b>
                </div>
                {c.goal2 ? (
                  <div>
                    <s>Дополнительная цель</s>
                    <b>{c.goal2}</b>
                  </div>
                ) : null}
              </div>
            </section>
            {tr ? (
              <section className="card ptr">
                <span className="pav sm">{tr.photo ? <img src={tr.photo} alt="" /> : initials(tr.first, tr.last)}</span>
                <div>
                  <s>Тренер</s>
                  <b>{[tr.first, tr.last].filter(Boolean).join(' ')}</b>
                  <span>
                    {tr.city ? tr.city + ' · ' : ''}с {fullDate(iso(new Date(c.createdAt as unknown as string)))}
                  </span>
                </div>
              </section>
            ) : null}
            <AchShelf ach={c.ach} streak={c.streak} today={today} onOpen={setAchOpen} onStreak={() => setStreakOpen(true)} />
          </>
        ) : null}
        <nav className="ptabs" role="tablist">
          <button role="tab" className={tab === 'pr' ? 'on' : ''} onClick={() => setTab('pr')}>
            Рекорды
          </button>
          <button role="tab" className={tab === 'meas' ? 'on' : ''} onClick={() => setTab('meas')}>
            Замеры
          </button>
        </nav>
        <div className="ptab">{tab === 'pr' ? <Records today={today} /> : <Measures rows={rows} today={today} />}</div>
      </div>
      <Tabs on="profile" />
      {c ? <EditSheet open={edit} onClose={() => setEdit(false)} /> : null}
      {c && achOpen ? (
        <AchFinal id={achOpen} open onClose={() => setAchOpen(null)} who={{ first: c.first, she: c.sex === 'f', trainer: tr ? [tr.first, tr.last].filter(Boolean).join(' ') : null }} />
      ) : null}
      {c ? (
        <StreakShow
          open={streakOpen}
          onClose={() => setStreakOpen(false)}
          from={c.streak.cur}
          to={c.streak.cur}
          milestone={false}
          record={false}
          quote={streakQuote(c.streak.cur)}
          who={{ first: c.first, she: c.sex === 'f', trainer: tr ? [tr.first, tr.last].filter(Boolean).join(' ') : null }}
        />
      ) : null}
    </>
  );
}

/* Рекорды: справа 1ПМ и изменение к прошлому, слева дата и 3ПМ; тап — график */
function Records({ today }: { today: string }) {
  const { rows, pm, pm3 } = useMaxes();
  const cat = useCatalog();
  const [sel, setSel] = useLocal<string | null>('pmSel', null);
  const [add, setAdd] = useState(false);
  /* Пустая строка (упражнение по умолчанию без рекорда) — «Новый рекорд» сразу на нём */
  const [addKey, setAddKey] = useState<string | null>(null);
  const [fix, setFix] = useState<MaxRow | null>(null);
  const pmKeys = useMe().data?.pmKeys || [];
  /* Свежие рекорды — сверху, пустые строки по умолчанию — за ними */
  const lastAt = (x: string) => histOf(rows, x).pop()?.[0] || '';
  const keys = Object.keys(pm).sort((a, b) => lastAt(b).localeCompare(lastAt(a)));
  const empty = pmKeys.filter((x) => pm[x] == null);
  if (!keys.length && !empty.length)
    return (
      <>
        <div className="card empty">
          <I n="trophy" />
          <b>Рекордов нет</b>
          <span>Добавьте 1ПМ — от него тренер считает вес в тренировках</span>
        </div>
        <button className="btn pri pbtn" onClick={() => setAdd(true)}>
          <I n="plus" />
          Добавить рекорд
        </button>
        <AddRecord open={add} onClose={() => setAdd(false)} sel={null} />
      </>
    );
  /* null — ещё не выбирали, '' — всё свёрнуто */
  const def = keys.find((x) => histOf(rows, x).length > 1) || keys[0] || '';
  const k = sel == null || (sel && !keys.includes(sel)) ? def : sel;
  const prev = (x: string) => {
    const vs = histOf(rows, x).map((e) => e[1]);
    const i = vs.length && vs[vs.length - 1] === pm[x] ? vs.length - 2 : vs.length - 1;
    return i >= 0 ? pm[x] - vs[i] : 0;
  };
  return (
    <>
      <div className="card plist acc">
        {keys.map((x) => {
          const on = x === k;
          const h = histOf(rows, x);
          const lx = h[h.length - 1];
          const d = prev(x);
          const d0 = h.length > 1 ? pm[x] - h[0][1] : 0;
          return (
            <div key={x} style={{ display: 'contents' }}>
              <button className={'prow' + (on ? ' on' : '')} onClick={() => setSel(on ? '' : x)} aria-expanded={on}>
                <span>
                  <b>{pmName(x, cat)}</b>
                  <s>
                    {lx ? 'обновлён ' + shortDate(lx[0], today) : 'без истории'}
                    {pm3[x] != null ? ' · 3ПМ ' + fmtN(pm3[x]) + ' кг' : ''}
                  </s>
                </span>
                <span className="pv">
                  {fmtN(pm[x])}
                  <i>кг</i>
                  {d ? <s className={'dlt ' + dltCls(d)}>{delta(d, 'кг')}</s> : null}
                </span>
                <I n="down" cls="chev" />
              </button>
              {on ? (
                <div className="pexp">
                  {h.length > 1 && d0 ? (
                    <div className="since l">
                      <span className={'dlt ' + dltCls(d0)}>{delta(d0, 'кг')}</span> с {shortDate(h[0][0], today)}
                    </div>
                  ) : null}
                  {h.length > 1 ? <Chart series={h} unit="кг" today={today} /> : <div className="nochart">История появится после второго рекорда</div>}
                  <div className="phist">
                    {rows
                      .filter((m) => m.key === x)
                      .slice()
                      .reverse()
                      .map((m, i) => {
                        const own = m.source === 'client' && !!m.id;
                        return (
                          <button key={m.id || i} className="phrow" disabled={!own} onClick={() => setFix(m)}>
                            <span>
                              {fullDate(m.date)}
                              {m.reps > 1 ? <s>{m.reps}ПМ</s> : null}
                              {own ? null : <s>внёс тренер</s>}
                            </span>
                            <b>
                              {fmtN(m.kg)}
                              <i>кг</i>
                            </b>
                            {own ? <I n="pen" /> : <i className="i" />}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
        {empty.map((x) => (
          <button key={x} className="prow" onClick={() => (setAddKey(x), setAdd(true))}>
            <span>
              <b>{pmName(x, cat)}</b>
              <s>не внесён</s>
            </span>
            <span className="pv">
              —<i>кг</i>
            </span>
            <I n="plus" cls="chev" />
          </button>
        ))}
      </div>
      <button className="btn pri pbtn" onClick={() => (setAddKey(null), setAdd(true))}>
        <I n="plus" />
        Новый рекорд
      </button>
      <p className="pnote">От 1ПМ тренер считает вес в тренировках: новый рекорд — новые веса. Тренер увидит изменение.</p>
      <AddRecord key={addKey || k} open={add} onClose={() => setAdd(false)} sel={addKey || k || null} />
      <EditRecord row={fix} onClose={() => setFix(null)} />
    </>
  );
}

/* Своя запись рекорда: поправить вес и дату или удалить (ошибся при вводе) */
function EditRecord({ row, onClose }: { row: MaxRow | null; onClose: () => void }) {
  const cat = useCatalog();
  const today = useToday();
  const utils = trpc.useUtils();
  const edit = trpc.clientApp.editMax.useMutation();
  const del = trpc.clientApp.delMax.useMutation();
  const [kg, setKg] = useState('');
  const [date, setDate] = useState('');
  const [sure, setSure] = useState(false);
  const [was, setWas] = useState<MaxRow | null>(null);
  if (row !== was) {
    setWas(row);
    if (row) {
      setKg(fmtN(row.kg));
      setDate(row.date);
      setSure(false);
    }
  }
  const done = async (t: string) => {
    await utils.clientApp.maxes.invalidate();
    onClose();
    toast(t);
  };
  const save = async () => {
    const a = num(kg);
    if (!row?.id || !a || !date) return;
    try {
      await edit.mutateAsync({ id: row.id, kg: a, date });
      await done('Рекорд исправлен — веса в тренировках пересчитаны');
    } catch {
      toast('Не сохранилось — проверьте связь');
    }
  };
  const remove = async () => {
    if (!row?.id) return;
    if (!sure) return setSure(true);
    try {
      await del.mutateAsync({ id: row.id });
      await done('Запись удалена');
    } catch {
      toast('Не удалилось — проверьте связь');
    }
  };
  const r = row || was;
  return (
    <FormSheet
      open={!!row}
      onClose={onClose}
      title={r ? pmName(r.key, cat) + (r.reps > 1 ? ' · ' + r.reps + 'ПМ' : '') : ''}
      ok={!!num(kg) && !!date}
      onSave={save}
      after={
        <div className="glist">
          <button className="srow rm" onClick={remove} disabled={del.isPending}>
            <span>{sure ? 'Точно удалить? Нажмите ещё раз' : 'Удалить запись'}</span>
          </button>
        </div>
      }
    >
      <div className="gr">
        <label htmlFor="x-d">Дата</label>
        <input id="x-d" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="gr">
        <label htmlFor="x-kg">{r && r.reps > 1 ? r.reps + 'ПМ' : '1ПМ'}</label>
        <input id="x-kg" inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} />
        <span className="unit">кг</span>
      </div>
    </FormSheet>
  );
}

/* «Новый рекорд»: упражнение, 1ПМ, 3ПМ по желанию */
function AddRecord({ open, onClose, sel }: { open: boolean; onClose: () => void; sel: string | null }) {
  const { pm } = useMaxes();
  const cat = useCatalog();
  const utils = trpc.useUtils();
  const add = trpc.clientApp.addMax.useMutation({ onSuccess: (r) => r.firstAch?.forEach((id) => enqueue({ kind: 'ach', id })) });
  const [ex, setEx] = useState<string>('');
  const [v1, setV1] = useState('');
  const [v3, setV3] = useState('');
  const [, setSel] = useLocal<string | null>('pmSel', null);
  /* С рекордом и строки по умолчанию — сверху, остальные движения с весом — по алфавиту */
  const top = [...Object.keys(pm), ...(useMe().data?.pmKeys || []).filter((x) => pm[x] == null)];
  const seen = new Set(top);
  const more: [string, string][] = [];
  for (const e of cat.list) {
    const k = pmKey(e);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    more.push([k, pmName(k, cat)]);
  }
  more.sort((a, b) => a[1].localeCompare(b[1], 'ru'));
  const opts: [string, string][] = [...top.map((k) => [k, pmName(k, cat)] as [string, string]), ...more];
  const key = ex || sel || opts[0]?.[0] || '';
  const save = async () => {
    const a = num(v1);
    const b = num(v3);
    if (!a || !key) return;
    try {
      await add.mutateAsync({ key, kg: a, reps: 1 });
      if (b) await add.mutateAsync({ key, kg: b, reps: 3 });
      await utils.clientApp.maxes.invalidate();
      setSel(key);
      onClose();
      setV1('');
      setV3('');
      toast('Рекорд сохранён — веса в тренировках пересчитаны');
    } catch {
      toast('Не сохранилось — проверьте связь');
    }
  };
  const [pick, setPick] = useState(false);
  const curName = opts.find(([k]) => k === key)?.[1] || '';
  return (
    <FormSheet open={open} onClose={onClose} title="Новый рекорд" ok={!!num(v1)} onSave={save}>
      <div className="gr">
        <label htmlFor="n-ex">Упражнение</label>
        {opts.length > 10 ? (
          <>
            <button id="n-ex" type="button" className="pk-go" onClick={() => setPick(true)}>
              {curName}
            </button>
            <PickSheet open={pick} onClose={() => setPick(false)} title="Упражнение" options={opts} value={key} onPick={setEx} />
          </>
        ) : (
          <select id="n-ex" value={key} onChange={(e) => setEx(e.target.value)}>
            {opts.map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
        )}
        <I n="down" />
      </div>
      <div className="gr">
        <label htmlFor="n-1">1ПМ</label>
        <input id="n-1" inputMode="decimal" placeholder={pm[key] ? fmtN(pm[key]) : '—'} value={v1} onChange={(e) => setV1(e.target.value)} />
        <span className="unit">кг</span>
      </div>
      <div className="gr">
        <label htmlFor="n-3">3ПМ</label>
        <input id="n-3" inputMode="decimal" placeholder="по желанию" value={v3} onChange={(e) => setV3(e.target.value)} />
        <span className="unit">кг</span>
      </div>
    </FormSheet>
  );
}

/* Замеры: показатель, последнее значение крупно, график, история по датам */
function Measures({ rows, today }: { rows: MRow[]; today: string }) {
  const [sel, setSel] = useLocal<MKey>('measSel', 'weight');
  const [add, setAdd] = useState(false);
  const [fix, setFix] = useState<MRow | null>(null);
  const [k, name, unit] = MEAS.find((m) => m[0] === sel) || MEAS[0];
  const dec = k === 'weight' || k === 'fat';
  const own = rows.filter((r) => r[k] != null);
  const ser = own.map((r) => [r.date, r[k] as number] as [string, number]);
  const chips = (
    <div className="mchips">
      {MEAS.map(([x, n]) => (
        <button key={x} className={x === k ? 'on' : ''} onClick={() => setSel(x)}>
          {n}
        </button>
      ))}
    </div>
  );
  const onSaved = (r: MRow) => r[sel] == null && MEAS.some(([x]) => r[x] != null) && setSel(MEAS.find(([x]) => r[x] != null)![0]);
  const sheet = (
    <>
      <AddMeasure open={add} onClose={() => setAdd(false)} rows={rows} onSaved={onSaved} />
      <AddMeasure open={!!fix} onClose={() => setFix(null)} rows={rows} onSaved={onSaved} row={fix} />
    </>
  );
  if (!ser.length)
    return (
      <>
        {chips}
        <div className="card empty">
          <I n="scale" />
          <b>Замеров нет</b>
          <span>Добавьте первый — будет видна динамика</span>
        </div>
        <button className="btn pri pbtn" onClick={() => setAdd(true)}>
          <I n="plus" />
          Добавить замер
        </button>
        {sheet}
      </>
    );
  const cur = ser[ser.length - 1];
  const d0 = cur[1] - ser[0][1];
  return (
    <>
      {chips}
      <div className="card pch">
        <div className="pchh">
          <div>
            <s>
              {name} · {shortDate(cur[0], today)}
            </s>
            <b>
              {fmtN(cur[1], dec)}
              <i>{unit}</i>
            </b>
          </div>
          <span className="since">
            {ser.length > 1 && d0 ? (
              <>
                <span className="dlt n">{delta(d0, unit, dec)}</span> с {shortDate(ser[0][0], today)}
              </>
            ) : null}
          </span>
        </div>
        {ser.length > 1 ? <Chart series={ser} unit={unit} today={today} /> : <div className="nochart">График появится после второго замера</div>}
      </div>
      <div className="card plist">
        {ser
          .map((r, i) => ({ r, d: i ? r[1] - ser[i - 1][1] : 0, m: own[i] }))
          .reverse()
          .map(({ r, d, m }, i) => (
            <button className="prow" key={(m.id || r[0]) + i} disabled={m.source !== 'client' || !m.id} onClick={() => setFix(m)}>
              <span>
                <b>{fullDate(r[0])}</b>
                {m.source !== 'client' ? <s>внёс тренер</s> : null}
              </span>
              <span className="pv">
                {fmtN(r[1], dec)}
                <i>{unit}</i>
                <s className={d ? 'n' : ''}>{delta(Math.round(d * 10) / 10, unit, dec)}</s>
              </span>
            </button>
          ))}
      </div>
      <button className="btn pri pbtn" onClick={() => setAdd(true)}>
        <I n="plus" />
        Добавить замер
      </button>
      {sheet}
    </>
  );
}

/* Новый замер; с row — правка своего замера (все показатели за ту дату) и удаление */
function AddMeasure({ open, onClose, rows, onSaved, row }: { open: boolean; onClose: () => void; rows: MRow[]; onSaved: (r: MRow) => void; row?: MRow | null }) {
  const today = useToday();
  const utils = trpc.useUtils();
  const add = trpc.clientApp.addMeasurement.useMutation({ onSuccess: (r) => r.firstAch?.forEach((id) => enqueue({ kind: 'ach', id })) });
  const edit = trpc.clientApp.editMeasurement.useMutation();
  const del = trpc.clientApp.delMeasurement.useMutation();
  const [date, setDate] = useState(today);
  const [v, setV] = useState<Record<string, string>>({});
  const [sure, setSure] = useState(false);
  const [was, setWas] = useState<MRow | null | undefined>(row);
  if (row !== was) {
    setWas(row);
    if (row) {
      setDate(row.date);
      setV(Object.fromEntries(MEAS.filter(([k]) => row[k] != null).map(([k]) => [k, fmtN(row[k] as number, k === 'weight' || k === 'fat')])));
      setSure(false);
    }
  }
  const remove = async () => {
    if (!row?.id) return;
    if (!sure) return setSure(true);
    try {
      await del.mutateAsync({ id: row.id });
      await utils.clientApp.measurements.invalidate();
      onClose();
      toast('Замер удалён');
    } catch {
      toast('Не удалилось — проверьте связь');
    }
  };
  const last = [...rows].reverse();
  const ph = (k: MKey) => {
    const r = last.find((x) => x[k] != null);
    return r ? fmtN(r[k] as number, k === 'weight' || k === 'fat') : '—';
  };
  const ok = !!date && MEAS.some(([k]) => num(v[k] || '') != null && num(v[k] || '')! > 0);
  const save = async () => {
    const r: MRow = { date };
    for (const [k] of MEAS) {
      const x = num(v[k] || '');
      if (x) r[k] = x;
    }
    try {
      if (row?.id) await edit.mutateAsync({ id: row.id, date, ...Object.fromEntries(MEAS.map(([k]) => [k, r[k] ?? null])) } as { id: string; date: string } & Record<MKey, number | null>);
      else await add.mutateAsync(r);
      await utils.clientApp.measurements.invalidate();
      onSaved(r);
      if (!row) setV({});
      onClose();
      toast(row ? 'Замер исправлен' : 'Замер сохранён');
    } catch {
      toast('Не сохранилось — проверьте связь');
    }
  };
  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={row ? 'Замер' : 'Новый замер'}
      ok={ok}
      onSave={save}
      after={
        row ? (
          <div className="glist">
            <button className="srow rm" onClick={remove} disabled={del.isPending}>
              <span>{sure ? 'Точно удалить? Нажмите ещё раз' : 'Удалить замер'}</span>
            </button>
          </div>
        ) : null
      }
    >
      <div className="gr">
        <label htmlFor="m-d">Дата</label>
        <input id="m-d" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      {MEAS.map(([k, n, u]) => (
        <div className="gr" key={k}>
          <label htmlFor={'m-' + k}>{n}</label>
          <input id={'m-' + k} inputMode="decimal" placeholder={ph(k)} value={v[k] || ''} onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))} />
          <span className="unit">{u}</span>
        </div>
      ))}
    </FormSheet>
  );
}

/* «Мои данные»: поля из создания аккаунта и цель */
function EditSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const today = useToday();
  const c = me.data!;
  const utils = trpc.useUtils();
  const upd = trpc.clientApp.updateProfile.useMutation();
  const init = () => ({
    last: c.last || '',
    first: c.first || '',
    sex: (c.sex || 'm') as 'm' | 'f',
    born: c.born || '',
    h: c.height ? fmtN(c.height) : '',
    level: c.level || LEVELS[0],
    goal: c.goal || GOALS[0],
    goal2: c.goal2 || '',
  });
  const [f, setF] = useState(init);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setF(init());
  }
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    try {
      await upd.mutateAsync({ last: f.last.trim(), first: f.first.trim(), sex: f.sex, ...(f.born ? { born: f.born } : {}), height: num(f.h), level: f.level, goal: f.goal, goal2: f.goal2.trim() || null });
      await utils.clientApp.me.invalidate();
      onClose();
      toast('Данные сохранены');
    } catch {
      toast('Не сохранилось — проверьте связь');
    }
  };
  const opt = (val: 'm' | 'f', label: string) => (
    <button type="button" className={'opt' + (f.sex === val ? ' on' : '')} onClick={() => set({ sex: val })}>
      {label}
    </button>
  );
  return (
    <FormSheet open={open} onClose={onClose} title="Мои данные" ok={!!f.first.trim() && !!f.last.trim()} onSave={save}>
      <div className="gr">
        <label htmlFor="e-last">Фамилия</label>
        <input id="e-last" autoComplete="family-name" value={f.last} onChange={(e) => set({ last: e.target.value })} />
      </div>
      <div className="gr">
        <label htmlFor="e-first">Имя</label>
        <input id="e-first" autoComplete="given-name" value={f.first} onChange={(e) => set({ first: e.target.value })} />
      </div>
      <div className="gr">
        <label>Пол</label>
        <div className="seg2">
          {opt('m', 'Мужской')}
          {opt('f', 'Женский')}
        </div>
      </div>
      <div className="gr">
        <label htmlFor="e-born">Дата рождения</label>
        <input id="e-born" type="date" max={today} value={f.born} onChange={(e) => set({ born: e.target.value })} />
      </div>
      <div className="gr">
        <label htmlFor="e-h">Рост</label>
        <input id="e-h" inputMode="decimal" placeholder="по желанию" value={f.h} onChange={(e) => set({ h: e.target.value })} />
        <span className="unit">см</span>
      </div>
      <div className="gr">
        <label htmlFor="e-lvl">Уровень</label>
        <select id="e-lvl" value={f.level} onChange={(e) => set({ level: e.target.value })}>
          {LEVELS.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <I n="down" />
      </div>
      <div className="gr">
        <label htmlFor="e-goal">Основная цель</label>
        <select id="e-goal" value={f.goal} onChange={(e) => set({ goal: e.target.value })}>
          {GOALS.map((g) => (
            <option key={g}>{g}</option>
          ))}
        </select>
        <I n="down" />
      </div>
      <div className="gr col">
        <label htmlFor="e-goal2">Дополнительная цель</label>
        <textarea id="e-goal2" rows={2} placeholder="по желанию" value={f.goal2} onChange={(e) => set({ goal2: e.target.value })} />
      </div>
    </FormSheet>
  );
}
